import React from 'react';
import PropTypes from 'prop-types';
import CheckIcon from '@mui/icons-material/Check';
import CreateIcon from '@mui/icons-material/Create';
import DownloadIcon from '@mui/icons-material/Download';

import Icon from 'utils/icons';
import moment from 'utils/moment';

import { HelpTooltip, Text } from 'components/DataDisplay';
import {
  Button,
  IconButton,
  Select,
  Switch,
  TextField
} from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';

import {
  AccordionSection,
  getMediaStatus,
  SetupMediaCard,
  TableConnectorComponent
} from './components';

const ModalSetup = ({ view }) => {
  const {
    campaign,
    themeColors,
    domainConfig,
    mediaOrders,
    concernedUpsertMedias,
    ads,
    activableMedias,
    mediaLogosToDisplay,
    mediaOrderMargin,
    mandatsCampaign,
    setMediaOrders,
    handleSaveMediaOrder,
    isCampaignSetupPending,
    filteredObjectives,
    selectedMediaOrderIds,
    onMediaOrderSelectedChange,
    setAds,
    handleSaveExternalId,
    handleSaveAd,
    hasMetaPage,
    isMeta,
    loaded,
    downloadAssets,
    canLinkMeta,
    isMetaLinked,
    linkMeta,
    canSetupGlobally,
    launchSetupCampaign,
    abortSetup,
    formattedPlatforms,
    isExpandedSetupAccount,
    setIsExpandedSetupAccount,
    setupPendingTooltip,
    mediaLogos,
    allPlatformAccounts,
    allPlatformPages,
    displayDrawerInput,
    handleChangeExternalId,
    activationShopGroups,
    blockedPagesMedias,
    getMediaModelByMedia,
    showDefaultGeoZoneConfig,
    isExpandedDefaultGeoZone,
    setIsExpandedDefaultGeoZone,
    defaultGeoZoneType,
    geoZones,
    setDefaultGeoZoneType,
    disabledGeoZone,
    overrideDisabledGeoZone,
    defaultGeoZoneValues,
    setDefaultGeoZoneValues,
    validateDefaultGeoZone,
    setDisabledGeoZone
  } = view;
  const { designSystem } = themeColors;

  const formatBroadcastDates = mediaOrder => {
    const start = moment(mediaOrder.startDate || campaign.start);
    const end = moment(mediaOrder.endDate || campaign.end);
    if (!start.isValid() || !end.isValid()) {
      return null;
    }
    if (start.isSame(end, 'day')) {
      return `Envoi unique · ${end.format('D MMMM')}`;
    }
    if (start.isSame(end, 'month') && start.isSame(end, 'year')) {
      return `${start.format('D')} → ${end.format('D MMMM')}`;
    }
    return `${start.format('D MMM')} → ${end.format('D MMM')}`;
  };

  const isLeadGenMedia = mediaOrder =>
    hasMetaPage[mediaOrder.activableMedia] &&
    isMeta(mediaOrder.activableMedia) &&
    (mediaOrder.objective
      ? mediaOrder.objective === 'li'
      : campaign.objective === 'li');

  return (
    <Container
      flex
      style={{ width: '100%', flexDirection: 'column', alignItems: 'stretch' }}
    >
      <Loading loading={!loaded} />
      {mediaOrders.map((mediaOrder, mediaOrderIndex) => {
        const activableMedia = activableMedias.find(
          el => el.key === mediaOrder.activableMedia
        );
        const mediaAds = ads.filter(ad => ad.mediaOrderId === mediaOrder.id);

        return (
          <SetupMediaCard
            key={mediaOrder.id}
            mediaOrder={mediaOrder}
            activableMedia={activableMedia}
            logo={mediaLogosToDisplay[mediaOrderIndex]}
            status={getMediaStatus(
              designSystem,
              activableMedia,
              mediaOrder.setupTask,
              mediaOrder.id,
              concernedUpsertMedias.includes(mediaOrder.activableMedia),
              campaign.lastUpsertTask
            )}
            dateLabel={formatBroadcastDates(mediaOrder)}
            total={mediaOrder.budgetMedia - mediaOrderMargin}
            mediaAds={mediaAds}
            mediaOrders={mediaOrders}
            setMediaOrders={setMediaOrders}
            handleSaveMediaOrder={handleSaveMediaOrder}
            setAds={setAds}
            handleSaveExternalId={handleSaveExternalId}
            handleSaveAd={handleSaveAd}
            isCampaignSetupPending={isCampaignSetupPending}
            isLeadGen={isLeadGenMedia(mediaOrder)}
            mandatsCampaign={mandatsCampaign}
            objectives={filteredObjectives}
            skipData={campaign.skipData}
            selected={selectedMediaOrderIds.includes(mediaOrder.id)}
            onSelectedChange={() => onMediaOrderSelectedChange(mediaOrder.id)}
          />
        );
      })}
      {showDefaultGeoZoneConfig && (
        <AccordionSection
          title="Configuration du ciblage géographique"
          tooltip={
            <HelpTooltip title="Le ciblage géographique est automatiquement appliqué en fonction des paramètres configurés sur le domaine." />
          }
          isExpanded={isExpandedDefaultGeoZone}
          onToggle={() => {
            setIsExpandedDefaultGeoZone(!isExpandedDefaultGeoZone);
          }}
        >
          <Row justify="flex-start" spacing={0}>
            <Item justify="flex-start" xs={2}>
              <Select
                title="Zone de chalandise"
                small
                value={defaultGeoZoneType}
                options={geoZones}
                onChange={value => {
                  setDefaultGeoZoneType(value);
                }}
                width="150px"
                disabled={disabledGeoZone || overrideDisabledGeoZone}
              />
            </Item>
            <Item justify="flex-start" xs={2} style={{ maxWidth: '150px' }}>
              <TextField
                title={
                  geoZones.find(el => el.value === defaultGeoZoneType)?.label ||
                  null
                }
                value={defaultGeoZoneValues}
                onChange={value => {
                  setDefaultGeoZoneValues(value);
                }}
                small
                disabled={disabledGeoZone || overrideDisabledGeoZone}
              />
            </Item>
            {disabledGeoZone || overrideDisabledGeoZone ? (
              <Item xs={2} justify="flex-start" style={{ marginTop: '15px' }}>
                <IconButton
                  onClick={() => {
                    setDisabledGeoZone(!disabledGeoZone);
                  }}
                >
                  <CreateIcon style={{ color: themeColors.light }} />
                </IconButton>{' '}
              </Item>
            ) : (
              <Item xs={2} justify="flex-start" style={{ marginTop: '15px' }}>
                <IconButton
                  onClick={async () => {
                    setDisabledGeoZone(!disabledGeoZone);
                    await validateDefaultGeoZone();
                  }}
                >
                  <CheckIcon style={{ color: themeColors.light }} />
                </IconButton>
              </Item>
            )}
          </Row>
        </AccordionSection>
      )}
      {canLinkMeta && (
        <Item
          flex
          style={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            margin: '4px 0 18px'
          }}
        >
          <Text
            noWrap
            fontWeight={600}
            fontSize="13px"
            color={themeColors.designSystem.inkSoft}
          >
            Dépense automatique Meta
          </Text>
          <Item
            flex
            style={{ alignItems: 'center', justifyContent: 'flex-end' }}
          >
            <Switch
              checked={isMetaLinked}
              options={['Non', 'Oui']}
              color={themeColors.black}
              onChange={linkMeta}
            />
          </Item>
        </Item>
      )}
      {formattedPlatforms.length > 0 ? (
        <AccordionSection
          title="Configuration des comptes"
          isExpanded={isExpandedSetupAccount}
          onToggle={() => {
            setIsExpandedSetupAccount(!isExpandedSetupAccount);
          }}
        >
          <TableConnectorComponent
            setupPendingTooltip={setupPendingTooltip}
            isCampaignSetupPending={isCampaignSetupPending}
            mediaLogos={mediaLogos}
            allPlatformAccounts={allPlatformAccounts}
            allPlatformPages={allPlatformPages}
            formattedPlatforms={formattedPlatforms}
            displayInput={displayDrawerInput}
            handleChangeExternalId={handleChangeExternalId}
            campaign={campaign}
            activationShopGroups={activationShopGroups}
            blockedPagesMedias={blockedPagesMedias}
            domainAccounts={domainConfig.platformAccounts}
            getMediaModelByMedia={getMediaModelByMedia}
            domainPages={domainConfig.platformPages}
            activableMedias={activableMedias}
          />
        </AccordionSection>
      ) : (
        <Loading loading={!loaded} />
      )}
      <Item
        flex
        style={{
          width: '100%',
          alignItems: 'center',
          gap: '10px',
          marginTop: '14px'
        }}
      >
        <Button
          variant="contained"
          size="medium"
          color={designSystem.ink}
          onClick={() => {
            downloadAssets();
          }}
        >
          <DownloadIcon />
          &nbsp;
          <Text fontSize="12px" fontWeight={600} color="inherit">
            Télécharger tous les assets
          </Text>
        </Button>
        <Item flex style={{ flex: 1 }} />
        {!isCampaignSetupPending ? (
          <Button
            variant="contained"
            size="medium"
            color={designSystem.green}
            disabled={!canSetupGlobally || selectedMediaOrderIds.length === 0}
            onClick={() => {
              launchSetupCampaign();
            }}
          >
            <Icon
              icon="setUpAutoIcon"
              attrs={{
                circleColor: themeColors.keep_light,
                arrowColor: !canSetupGlobally
                  ? themeColors.themeV2.subtitle
                  : designSystem.green
              }}
            />
            &nbsp;
            <Text fontSize="12px" fontWeight={600} color="inherit">
              Lancer le setup sur les médias sélectionnés
            </Text>
          </Button>
        ) : (
          <Button
            variant="contained"
            size="medium"
            color={themeColors.error}
            fontColor={themeColors.keep_light}
            onClick={() => {
              abortSetup();
            }}
          >
            <Icon
              icon="cancelSetup"
              attrs={{
                width: '31px',
                height: '31px',
                fill: themeColors.keep_light
              }}
            />
            &nbsp;
            <Text fontSize="12px" fontWeight={600} color="inherit">
              Avorter le setup
            </Text>
          </Button>
        )}
      </Item>
    </Container>
  );
};

ModalSetup.propTypes = {
  view: PropTypes.shape().isRequired
};

export default ModalSetup;
